if (!customElements.get('shipping-cutoff-timer')) {
  const UTC_TIME = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });

  const SHIPPING_WEEKDAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);

  function getUtcTimeParts(date) {
    return Object.fromEntries(
      UTC_TIME.formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );
  }

  function getRemainingSecondsUntilCutoff(cutoffHour, date = new Date()) {
    const parts = getUtcTimeParts(date);

    if (!SHIPPING_WEEKDAYS.has(parts.weekday)) return 0;

    const elapsedSeconds =
      Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    const remainingSeconds = cutoffHour * 3600 - elapsedSeconds;

    return Math.max(0, remainingSeconds);
  }

  function isWeekend(date = new Date()) {
    const { weekday } = getUtcTimeParts(date);
    return !SHIPPING_WEEKDAYS.has(weekday);
  }

  function formatCountdown(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  }

  class ShippingCutoffTimer extends HTMLElement {
    connectedCallback() {
      this.cutoffHour = Number(this.dataset.cutoffHour) || 14;

      try {
        this.i18n = JSON.parse(this.querySelector('[data-shipping-cutoff-i18n]')?.textContent || '{}');
      } catch {
        this.i18n = {};
      }

      this.linePrimary = this.querySelector('[data-shipping-cutoff-line-primary]');
      this.lineSecondary = this.querySelector('[data-shipping-cutoff-line-secondary]');
      this.timeEl = this.querySelector('[data-shipping-cutoff-time]');

      this.update();
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    setWeekdayView(remainingSeconds) {
      const time = formatCountdown(remainingSeconds);
      const prefix = this.i18n.weekday_line_1 || '';
      const suffix = this.i18n.weekday_line_2 || '';

      if (this.timeEl) {
        this.timeEl.textContent = time;
        this.timeEl.hidden = false;
      }

      if (this.linePrimary) {
        this.linePrimary.textContent = prefix ? `${prefix} ` : '';
        this.linePrimary.hidden = false;
      }

      if (this.lineSecondary) {
        this.lineSecondary.textContent = suffix;
        this.lineSecondary.hidden = suffix === '';
      }

      this.hidden = false;
    }

    setWeekendView() {
      if (this.linePrimary) {
        this.linePrimary.textContent = this.i18n.weekend_line_1 || '';
        this.linePrimary.hidden = false;
      }

      if (this.timeEl) {
        this.timeEl.hidden = true;
      }

      if (this.lineSecondary) {
        this.lineSecondary.textContent = this.i18n.weekend_line_2 || '';
        this.lineSecondary.hidden = false;
      }

      this.hidden = false;
    }

    update() {
      if (this.dataset.eligible !== 'true') {
        this.hidden = true;
        return;
      }

      if (isWeekend()) {
        this.setWeekendView();
        return;
      }

      const remainingSeconds = getRemainingSecondsUntilCutoff(this.cutoffHour);

      if (remainingSeconds <= 0) {
        this.setWeekendView();
        return;
      }

      this.setWeekdayView(remainingSeconds);
    }
  }

  customElements.define('shipping-cutoff-timer', ShippingCutoffTimer);
}
